import { useState, useEffect } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  InputAdornment,
} from '@mui/material'
import {
  Add,
  Check,
  Close,
  PlayArrow,
  AssignmentReturn,
  Cancel,
  OpenInNew,
  CheckCircle,
  Block,
} from '@mui/icons-material'
import { Link } from 'react-router-dom'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const STATUS_COLORS = {
  requested: 'warning',
  approved: 'info',
  rejected: 'error',
  in_use: 'primary',
  returned: 'success',
  cancelled: 'default',
}

const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'needs_maintenance', label: 'Needs Maintenance' },
]

const columns = [
  { field: 'request_number', headerName: 'Request #', sortable: true, width: 130 },
  { field: 'vehicle_registration', headerName: 'Vehicle', sortable: true, width: 120 },
  { field: 'vehicle_name', headerName: 'Name', sortable: true },
  { field: 'destination', headerName: 'Destination', sortable: true },
  {
    field: 'departure_date',
    headerName: 'Departure',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value?.replace('_', ' ').toUpperCase()}
        color={STATUS_COLORS[value] || 'default'}
        size="small"
      />
    ),
  },
  { field: 'requested_by_name', headerName: 'Requested By', sortable: true },
  {
    field: 'created_at',
    headerName: 'Date',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
]

const initialFormData = {
  vehicle: '',
  purpose: '',
  destination: '',
  departure_date: '',
  expected_return_date: '',
}

const VehicleRequestsList = () => {
  const { hasPermission } = usePermissions()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Action dialogs
  const [startTripOpen, setStartTripOpen] = useState(false)
  const [returnOpen, setReturnOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [mileageOut, setMileageOut] = useState('')
  const [mileageReturn, setMileageReturn] = useState('')
  const [conditionOnReturn, setConditionOnReturn] = useState('good')
  const [faultsEncountered, setFaultsEncountered] = useState('')
  const [returnNotes, setReturnNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')

  // Options for dropdowns
  const [vehicles, setVehicles] = useState([])

  const fetchRequests = async () => {
    try {
      setLoading(true)
      const response = await api.get('/assets/vehicle-requests/')
      setRequests(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching vehicle requests:', error)
      toast.error('Failed to load vehicle requests')
    } finally {
      setLoading(false)
    }
  }

  const fetchVehicles = async () => {
    try {
      // Fetch only active vehicles - inactive vehicles cannot be booked
      const response = await api.get('/assets/vehicles/', { params: { is_active: true } })
      const vehicleList = response.data.results || response.data
      setVehicles(vehicleList)
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      toast.error('Failed to load vehicles')
    }
  }

  const isVehicleAvailable = (vehicle) => {
    return vehicle.status === 'operational'
  }

  const getVehicleStatusLabel = (vehicle) => {
    if (vehicle.status === 'operational') return 'Available'
    return vehicle.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unavailable'
  }

  useEffect(() => {
    fetchRequests()
    fetchVehicles()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async () => {
    const newErrors = {}
    if (!formData.vehicle) newErrors.vehicle = 'Vehicle is required'
    if (!formData.purpose) newErrors.purpose = 'Purpose is required'
    if (!formData.destination) newErrors.destination = 'Destination is required'
    if (!formData.departure_date) newErrors.departure_date = 'Departure date is required'
    if (!formData.expected_return_date) newErrors.expected_return_date = 'Expected return date is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setFormLoading(true)
    try {
      await api.post('/assets/vehicle-requests/', formData)
      toast.success('Vehicle request submitted successfully')
      fetchRequests()
      fetchVehicles()
      setFormOpen(false)
      setFormData(initialFormData)
    } catch (error) {
      console.error('Error creating request:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        toast.error('Please fix the errors in the form')
      } else {
        toast.error('Failed to create vehicle request')
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleApprove = async () => {
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/approve/`)
      toast.success('Request approved successfully')
      fetchRequests()
      setDetailOpen(false)
    } catch (error) {
      console.error('Error approving request:', error)
      toast.error(error.response?.data?.error || 'Failed to approve request')
    }
  }

  const handleReject = async () => {
    if (!rejectionReason) {
      toast.error('Rejection reason is required')
      return
    }
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/reject/`, {
        reason: rejectionReason,
      })
      toast.success('Request rejected')
      fetchRequests()
      setDetailOpen(false)
      setRejectOpen(false)
      setRejectionReason('')
    } catch (error) {
      console.error('Error rejecting request:', error)
      toast.error(error.response?.data?.error || 'Failed to reject request')
    }
  }

  const handleStartTrip = async () => {
    if (!mileageOut) {
      toast.error('Mileage out is required')
      return
    }
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/start_trip/`, {
        mileage_out: parseInt(mileageOut),
      })
      toast.success('Trip started successfully')
      fetchRequests()
      fetchVehicles()
      setDetailOpen(false)
      setStartTripOpen(false)
      setMileageOut('')
    } catch (error) {
      console.error('Error starting trip:', error)
      toast.error(error.response?.data?.error || 'Failed to start trip')
    }
  }

  const handleRecordReturn = async () => {
    if (!mileageReturn) {
      toast.error('Return mileage is required')
      return
    }
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/record_return/`, {
        mileage_return: parseInt(mileageReturn),
        condition_on_return: conditionOnReturn,
        faults_encountered: faultsEncountered,
        return_notes: returnNotes,
      })
      toast.success('Return recorded successfully')
      fetchRequests()
      fetchVehicles()
      setDetailOpen(false)
      setReturnOpen(false)
      setMileageReturn('')
      setConditionOnReturn('good')
      setFaultsEncountered('')
      setReturnNotes('')
    } catch (error) {
      console.error('Error recording return:', error)
      toast.error(error.response?.data?.error || 'Failed to record return')
    }
  }

  const handleCancel = async () => {
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/cancel/`)
      toast.success('Request cancelled')
      fetchRequests()
      setDetailOpen(false)
    } catch (error) {
      console.error('Error cancelling request:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel request')
    }
  }

  const handleRowClick = (row) => {
    setSelectedRequest(row)
    setDetailOpen(true)
  }

  const getActionButtons = () => {
    if (!selectedRequest) return null

    switch (selectedRequest.status) {
      case 'requested':
        return (
          <>
            <PermissionGate permission="vehicle_requests.approve">
              <Button
                startIcon={<Check />}
                color="success"
                onClick={handleApprove}
              >
                Approve
              </Button>
              <Button
                startIcon={<Close />}
                color="error"
                variant="outlined"
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </PermissionGate>
            <Button
              startIcon={<Cancel />}
              variant="outlined"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </>
        )
      case 'approved':
        return (
          <>
            <PermissionGate permission="vehicle_requests.start_trip">
              <Button
                startIcon={<PlayArrow />}
                onClick={() => setStartTripOpen(true)}
              >
                Start Trip
              </Button>
            </PermissionGate>
            <Button
              startIcon={<Cancel />}
              variant="outlined"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </>
        )
      case 'in_use':
        return (
          <PermissionGate permission="vehicle_requests.record_return">
            <Button
              startIcon={<AssignmentReturn />}
              color="success"
              onClick={() => setReturnOpen(true)}
            >
              Record Return
            </Button>
          </PermissionGate>
        )
      default:
        return null
    }
  }

  return (
    <Box>
      <PageHeader
        title="Vehicle Requests"
        subtitle="Request and track vehicle usage"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'Vehicle Requests' },
        ]}
        actions={
          <PermissionGate permission="vehicle_requests.request">
            <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
              New Request
            </Button>
          </PermissionGate>
        }
      />

      <Card>
        <DataTable
          columns={columns}
          data={requests}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search requests..."
          emptyMessage="No vehicle requests found"
        />
      </Card>

      {/* Create Request Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Vehicle Request</DialogTitle>
        <DialogContent>
          {(errors.error || errors.detail || errors.non_field_errors) && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errors.error || errors.detail || errors.non_field_errors}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.vehicle)} required>
                <InputLabel>Vehicle</InputLabel>
                <Select
                  name="vehicle"
                  value={formData.vehicle}
                  onChange={handleChange}
                  label="Vehicle"
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {vehicles.length === 0 ? 'Loading vehicles...' : 'Select a vehicle'}
                  </MenuItem>
                  {vehicles.map((vehicle) => (
                    <MenuItem
                      key={vehicle.id}
                      value={vehicle.id}
                      disabled={!isVehicleAvailable(vehicle)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        opacity: isVehicleAvailable(vehicle) ? 1 : 0.6,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isVehicleAvailable(vehicle) ? (
                          <CheckCircle fontSize="small" color="success" />
                        ) : (
                          <Block fontSize="small" color="disabled" />
                        )}
                        <span>{vehicle.registration_number} - {vehicle.name}</span>
                      </Box>
                      <Chip
                        label={getVehicleStatusLabel(vehicle)}
                        size="small"
                        color={isVehicleAvailable(vehicle) ? 'success' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {errors.vehicle && (
                  <Typography variant="caption" color="error">{errors.vehicle}</Typography>
                )}
              </FormControl>
            </Grid>
            {formData.vehicle && (
              <Grid item xs={12}>
                <Link
                  to={`/assets/vehicles`}
                  target="_blank"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, color: '#1B5E7A' }}
                >
                  <OpenInNew fontSize="small" />
                  View vehicle details
                </Link>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                name="destination"
                value={formData.destination}
                onChange={handleChange}
                error={Boolean(errors.destination)}
                helperText={errors.destination}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Departure Date & Time"
                name="departure_date"
                type="datetime-local"
                value={formData.departure_date}
                onChange={handleChange}
                error={Boolean(errors.departure_date)}
                helperText={errors.departure_date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Return Date & Time"
                name="expected_return_date"
                type="datetime-local"
                value={formData.expected_return_date}
                onChange={handleChange}
                error={Boolean(errors.expected_return_date)}
                helperText={errors.expected_return_date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                error={Boolean(errors.purpose)}
                helperText={errors.purpose}
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Request {selectedRequest?.request_number}
          {selectedRequest && (
            <Chip
              label={selectedRequest.status?.replace('_', ' ').toUpperCase()}
              color={STATUS_COLORS[selectedRequest.status] || 'default'}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Vehicle</Typography>
                <Typography>{selectedRequest.vehicle_registration} - {selectedRequest.vehicle_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Requested By</Typography>
                <Typography>{selectedRequest.requested_by_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Department</Typography>
                <Typography>{selectedRequest.department_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Destination</Typography>
                <Typography>{selectedRequest.destination}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Purpose</Typography>
                <Typography>{selectedRequest.purpose}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Schedule</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Departure Date</Typography>
                <Typography>{selectedRequest.departure_date ? new Date(selectedRequest.departure_date).toLocaleString() : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                <Typography>{selectedRequest.expected_return_date ? new Date(selectedRequest.expected_return_date).toLocaleString() : 'N/A'}</Typography>
              </Grid>

              {selectedRequest.approved_by_name && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Approval</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Approved By</Typography>
                    <Typography>{selectedRequest.approved_by_name}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Approved Date</Typography>
                    <Typography>{selectedRequest.approved_date ? new Date(selectedRequest.approved_date).toLocaleString() : 'N/A'}</Typography>
                  </Grid>
                  {selectedRequest.approval_notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography>{selectedRequest.approval_notes}</Typography>
                    </Grid>
                  )}
                </>
              )}

              {selectedRequest.rejection_reason && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error">Rejection Reason</Typography>
                  <Typography color="error">{selectedRequest.rejection_reason}</Typography>
                </Grid>
              )}

              {selectedRequest.actual_departure_date && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Trip Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Actual Departure</Typography>
                    <Typography>{new Date(selectedRequest.actual_departure_date).toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Mileage Out</Typography>
                    <Typography>{selectedRequest.mileage_out?.toLocaleString()} km</Typography>
                  </Grid>
                </>
              )}

              {selectedRequest.actual_return_date && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Return Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Actual Return</Typography>
                    <Typography>{new Date(selectedRequest.actual_return_date).toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Mileage Return</Typography>
                    <Typography>{selectedRequest.mileage_return?.toLocaleString()} km</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Distance Traveled</Typography>
                    <Typography>{selectedRequest.distance_traveled?.toLocaleString()} km</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Days Outside</Typography>
                    <Typography>{selectedRequest.days_outside_compound} days</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Condition on Return</Typography>
                    <Chip
                      label={selectedRequest.condition_on_return_display || selectedRequest.condition_on_return}
                      color={selectedRequest.condition_on_return === 'good' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Grid>
                  {selectedRequest.faults_encountered && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="error">Faults Encountered</Typography>
                      <Typography color="error">{selectedRequest.faults_encountered}</Typography>
                    </Grid>
                  )}
                  {selectedRequest.return_notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Return Notes</Typography>
                      <Typography>{selectedRequest.return_notes}</Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          {getActionButtons()}
        </DialogActions>
      </Dialog>

      {/* Start Trip Dialog */}
      <Dialog open={startTripOpen} onClose={() => setStartTripOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Start Trip</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Mileage (km)"
            type="number"
            value={mileageOut}
            onChange={(e) => setMileageOut(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setStartTripOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleStartTrip}>
            Start Trip
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Return Dialog */}
      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Vehicle Return</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Return Mileage (km)"
                type="number"
                value={mileageReturn}
                onChange={(e) => setMileageReturn(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">km</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Condition on Return</InputLabel>
                <Select
                  value={conditionOnReturn}
                  onChange={(e) => setConditionOnReturn(e.target.value)}
                  label="Condition on Return"
                >
                  {CONDITION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Faults Encountered"
                value={faultsEncountered}
                onChange={(e) => setFaultsEncountered(e.target.value)}
                multiline
                rows={2}
                placeholder="Describe any faults or issues encountered during the trip"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Return Notes"
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReturnOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRecordReturn} color="success">
            Record Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRejectOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleReject} color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VehicleRequestsList
